import cn from "classnames"

import { useGetAdrState } from "@/api/useIncidents"
import { Collapse, Flex, Switch, Text } from "@/atoms"
import * as AdrDomain from "@/domain/adr"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { Heading } from "@/molecules/Heading"
import { PulsingDotLabel } from "@/molecules/PulsingDotLabel"
import {
  useAdrIncidentsList,
  useEnvironmentsForAdrIncident,
  useRulesForAdrIncident
} from "@/pages/TriagePage/application/useAdrIncidents"
import { EnvironmentCard } from "@/pages/TriagePage/components/pages/AdrManagementPage/components/molecules/EnvironmentCard"
import { useDataContext } from "@/pages/TriagePage/hooks/useData"
import { useFlash } from "@/pages/TriagePage/hooks/useFlash"
import { RuleList } from "."

import styles from "./DetailsSection.module.scss"

import type { DetailsSectionProps } from "./DetailsSection.types"

export const DetailsSection = ({ config }: DetailsSectionProps) => {
  const rulesContext = useRulesForAdrIncident(config)
  const environmentsContext = useEnvironmentsForAdrIncident(config)
  const { data: environments = [], countIncidents } =
    environmentsContext.response || {}
  const { response: adrStateResponse, isLoading: isAdrStateLoading } =
    useGetAdrState(config)
  const getSize = (length: number) => {
    if (length > 96) return "xs"
    if (length > 16) return "sm"

    return "default"
  }
  const size = getSize(environments.length)
  const context = useAdrIncidentsList(config)
  const isFlash = useFlash(context)

  const isLoading =
    rulesContext.isLoading || environmentsContext.isLoading || isAdrStateLoading
  const monitoringKey = "monitoring"
  const { logger, EVENTS } = useLogger()
  const { t } = useTranslation()
  const { t: tManagement } = useTranslation("translation", {
    keyPrefix: "triage.adr.management"
  })
  const { details, toggleDetails } = useDataContext()
  const disabled = AdrDomain.isPrerequisitesDegradedState(
    adrStateResponse?.data?.state
  )

  return (
    <Collapse
      ghost
      rootClassName={cn(
        styles.collapseRootClassName,
        environmentsContext.response?.hasIncidents === true &&
          styles.hasIncident,
        isFlash && styles.flashing
      )}
      collapsible="icon"
      expandIcon={() => null}
      activeKey={details ? monitoringKey : ""}
      items={[
        {
          key: monitoringKey,
          label: (
            <Flex align="center" justify="space-between">
              <PulsingDotLabel
                disabled={disabled}
                text={
                  <Flex gap={12} align="center">
                    <Heading
                      title={
                        disabled
                          ? tManagement("paused")
                          : tManagement("monitoring")
                      }
                      level={6}
                      classes={{ title: styles.monitoring }}
                    />
                    {!!countIncidents && !isFlash && (
                      <Text className={styles.incidentCount}>
                        {tManagement("potentialExploitation", {
                          count: countIncidents
                        })}
                      </Text>
                    )}
                  </Flex>
                }
              />
              <Flex gap={12} align="center">
                <span
                  className={cn(
                    styles.textDetails,
                    isLoading && styles.textDetailsLoading
                  )}
                >
                  {t("general.details")}
                </span>
                <Switch
                  size="small"
                  disabled={isLoading}
                  checked={details}
                  onChange={() => {
                    logger.info(
                      EVENTS.ANALYTIC_EVENTS.ADR.SWITCH_DETAILS_SECTION,
                      { open: !details }
                    )

                    toggleDetails()
                  }}
                />
              </Flex>
            </Flex>
          ),
          children: (
            <Flex vertical gap={24}>
              <Flex vertical gap={8}>
                <Heading
                  title={`${environments.length} ${tManagement("monitoredEnvironments")}`}
                  level={6}
                />
                <div
                  className={cn(
                    styles.wrapper,
                    size === "default" && styles.wrapper4,
                    size === "sm" && cn(styles.wrapperCommon, styles.wrapperSm),
                    size === "xs" && cn(styles.wrapperCommon, styles.wrapperXs)
                  )}
                >
                  {environments.map((data, index) => (
                    <EnvironmentCard size={size} key={index} {...data} />
                  ))}
                </div>
              </Flex>
              <Flex vertical gap={8}>
                <Heading title={tManagement("detectionTypes")} level={6} />
                <RuleList config={config} />
              </Flex>
            </Flex>
          )
        }
      ]}
    />
  )
}
